import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, UserPlus, X, Check, Landmark, Save } from 'lucide-react';
import { salespersonAPI, companySettingsAPI } from '../../services/api';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const OthersTab = () => {
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', designation: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const data = await salespersonAPI.getAll();
      setSalespersons(data);
    } catch { toast.error('Failed to load salespersons'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', designation: '' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (sp) => {
    setForm({ name: sp.name, email: sp.email || '', phone: sp.phone || '', designation: sp.designation || '' });
    setEditing(sp.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await salespersonAPI.update(editing, form);
        toast.success('Salesperson updated');
      } else {
        await salespersonAPI.create(form);
        toast.success('Salesperson added');
      }
      resetForm();
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this salesperson?')) return;
    try {
      await salespersonAPI.delete(id);
      toast.success('Salesperson removed');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="others-tab">
      {/* Salesperson Management */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#eef2f8' }}>
              <UserPlus className="w-4 h-4" style={{ color: '#24549c' }} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Sales Persons</h3>
              <p className="text-[11px] text-gray-500">Manage sales team members who can be assigned to customers</p>
            </div>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} data-testid="add-salesperson-btn"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
              <Plus className="w-3.5 h-3.5" /> Add Sales Person
            </button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200" data-testid="salesperson-form">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-gray-700">{editing ? 'Edit Sales Person' : 'Add Sales Person'}</h4>
              <button onClick={resetForm} className="p-1 rounded hover:bg-gray-200"><X className="w-3.5 h-3.5 text-gray-500" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Name *</label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" className="text-xs" data-testid="sp-name-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Email</label>
                <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="text-xs" data-testid="sp-email-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Phone</label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" className="text-xs" data-testid="sp-phone-input" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Designation</label>
                <Input value={form.designation} onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. Regional Manager" className="text-xs" data-testid="sp-designation-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={resetForm} className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting} data-testid="sp-save-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {editing ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* Salesperson Table */}
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : salespersons.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-xs">No sales persons added yet. Click "Add Sales Person" to get started.</div>
        ) : (
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <table className="w-full" data-testid="salesperson-table">
              <thead>
                <tr className="text-[10px] font-bold text-gray-500 uppercase bg-gray-50">
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Phone</th>
                  <th className="text-left px-4 py-2.5">Designation</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {salespersons.map(sp => (
                  <tr key={sp.id} className="border-t border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 text-xs font-semibold text-gray-900">{sp.name}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{sp.email || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600">{sp.phone || '-'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{sp.designation || '-'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleEdit(sp)} className="p-1 rounded hover:bg-blue-50" data-testid={`edit-sp-${sp.id}`}>
                          <Edit2 className="w-3 h-3 text-blue-600" />
                        </button>
                        <button onClick={() => handleDelete(sp.id)} className="p-1 rounded hover:bg-red-50" data-testid={`delete-sp-${sp.id}`}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Company Bank Details */}
      <CompanyBankDetails />
    </div>
  );
};


const CompanyBankDetails = () => {
  const [bankDetails, setBankDetails] = useState({
    bank_name: '', account_number: '', ifsc_code: '', branch_name: '', account_holder: '', upi_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    companySettingsAPI.getBankDetails()
      .then(data => { setBankDetails(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const startEditing = () => {
    setEditForm({ ...bankDetails });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await companySettingsAPI.updateBankDetails(editForm);
      setBankDetails(updated);
      setEditing(false);
      toast.success('Bank details saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const hasData = bankDetails.bank_name || bankDetails.account_number;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="company-bank-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#eef2f8' }}>
            <Landmark className="w-4 h-4" style={{ color: '#24549c' }} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Company Bank Details</h3>
            <p className="text-[11px] text-gray-500">Bank information used in Proforma Invoices & documents</p>
          </div>
        </div>
        {!editing && (
          <button onClick={startEditing} data-testid="edit-bank-btn"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
            <Edit2 className="w-3 h-3" /> {hasData ? 'Edit' : 'Add Details'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : editing ? (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="bank-edit-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Account Holder Name</label>
              <Input value={editForm.account_holder} onChange={e => setEditForm(f => ({ ...f, account_holder: e.target.value }))}
                placeholder="e.g. Sharda Infrasolutions Pvt. Ltd." className="text-xs" data-testid="bank-holder-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Bank Name *</label>
              <Input value={editForm.bank_name} onChange={e => setEditForm(f => ({ ...f, bank_name: e.target.value }))}
                placeholder="e.g. State Bank of India" className="text-xs" data-testid="bank-name-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Account Number *</label>
              <Input value={editForm.account_number} onChange={e => setEditForm(f => ({ ...f, account_number: e.target.value }))}
                placeholder="e.g. 12345678901234" className="text-xs font-mono" data-testid="bank-account-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">IFSC Code *</label>
              <Input value={editForm.ifsc_code} onChange={e => setEditForm(f => ({ ...f, ifsc_code: e.target.value.toUpperCase() }))}
                placeholder="e.g. SBIN0001234" className="text-xs font-mono" data-testid="bank-ifsc-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">Branch Name</label>
              <Input value={editForm.branch_name} onChange={e => setEditForm(f => ({ ...f, branch_name: e.target.value }))}
                placeholder="e.g. Nariman Point, Mumbai" className="text-xs" data-testid="bank-branch-input" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-semibold mb-1 block">UPI ID</label>
              <Input value={editForm.upi_id} onChange={e => setEditForm(f => ({ ...f, upi_id: e.target.value }))}
                placeholder="e.g. company@upi" className="text-xs" data-testid="bank-upi-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-bank-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          </div>
        </div>
      ) : hasData ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="bank-details-display">
          {[
            { label: 'Account Holder', value: bankDetails.account_holder },
            { label: 'Bank Name', value: bankDetails.bank_name },
            { label: 'Account Number', value: bankDetails.account_number, mono: true },
            { label: 'IFSC Code', value: bankDetails.ifsc_code, mono: true },
            { label: 'Branch', value: bankDetails.branch_name },
            { label: 'UPI ID', value: bankDetails.upi_id },
          ].filter(f => f.value).map(f => (
            <div key={f.label} className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-0.5">{f.label}</p>
              <p className={`text-xs font-semibold text-gray-900 ${f.mono ? 'font-mono' : ''}`}>{f.value}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-400 text-xs">No bank details added yet. Click "Add Details" to set up your company bank information.</div>
      )}
    </div>
  );
};

export default OthersTab;
